import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent, HttpService } from '@app/core';
import { TrackingService } from '@app/core/tracking.service';
import { ID } from '@app/models';
import { Question } from '@app/models/question';
import { VideoSettings } from '@app/models/video';
import { CollectedAnswer } from '@app/page-modules/exercise-task-module/exercise-task-module.component';

import { UntilDestroy } from '@ngneat/until-destroy';

interface VideoQuestionResult {
  question_id: ID;
  is_correct: boolean;
}

interface VideoQuestionSetResult {
  passed: boolean;
  count_correct: number;
  count_needed: number;
  count_questions: number;
  results: VideoQuestionResult[];
}

interface VideoQuestionResultRow {
  number?: string;
  text?: string;
  is_correct: boolean;
}

@UntilDestroy()
@Component({
  selector: 'video-question-set-dialog',
  templateUrl: 'video-question-set-dialog.component.html',
})
export class VideoQuestionSetDialogComponent
  extends BaseComponent
  implements OnInit
{
  public isInit = false;

  public questions: Question[] = [];
  public settings?: VideoSettings;
  public questionSetBaseUrl = '';

  public currentIndex = 0;
  public collectedAnswers: CollectedAnswer[] = [];

  public submitting = false;
  public resultMode = false;
  public result?: VideoQuestionSetResult;
  public dataSource?: MatTableDataSource<VideoQuestionResultRow>;
  public displayedColumns: string[] = ['question', 'status'];

  constructor(
    public dialogRef: MatDialogRef<VideoQuestionSetDialogComponent>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpService,
    private trackingService: TrackingService,
  ) {
    super();
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.questions = this.data.questions ?? [];
    this.settings = this.data.settings;
    this.questionSetBaseUrl = this.data.questionSetBaseUrl;
    this.isInit = true;
  }

  get currentQuestion(): Question {
    return this.questions[this.currentIndex];
  }

  onAnswerCollected(answer: CollectedAnswer): void {
    this.collectedAnswers.push(answer);
    if (this.currentIndex < this.questions.length - 1) {
      this.currentIndex++;
    } else {
      this.submit();
    }
  }

  submit(): void {
    this.submitting = true;
    this.addSubscription(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.http.patch(`${this.questionSetBaseUrl}/solve`, {
        answers: this.collectedAnswers,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ).subscribe((response: any) => {
      this.result = response?.data;
      const rows: VideoQuestionResultRow[] = (this.result?.results ?? []).map(
        (item) => {
          const question = this.questions.find(
            (q) => q.id === item.question_id,
          );
          return {
            number: question?.question_number,
            text: question?.question_text,
            is_correct: item.is_correct,
          };
        },
      );
      this.dataSource = new MatTableDataSource(rows);
      this.resultMode = true;
      this.submitting = false;
    });
  }

  // Schließen-Button ("X"): Quiz abbrechen – trackt die aktuell angezeigte Frage
  // als QUESTION_ABORT, bereits gesammelte (unsubmittete) Antworten verfallen.
  abort(): void {
    if (!this.resultMode && !this.submitting && this.currentQuestion) {
      this.trackingService.trackQuestionAbort(
        String(this.currentQuestion.id),
        this.questionSetBaseUrl,
      );
    }
    this.close('aborted');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  close(data?: any): void {
    this.dialogRef.close(data);
  }
}
